import { Injectable, PLATFORM_ID, computed, effect, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpHeaders } from '@angular/common/http';
import { Observable, catchError, forkJoin, map, of, tap, timeout } from 'rxjs';
import type { CountryCode } from 'libphonenumber-js';
import type { WorkBook } from 'xlsx';
import { ApiService } from '../api/api.service';
import { UserProfileService } from '../user/user-profile.service';
import { SettingsService } from '../settings/settings.service';
import { IUserOrganization } from '../../types/settingsDataModule';
import {
  ISmartBroadcastColumn,
  ISmartBroadcastDraft,
  ISmartBroadcastNumberStats,
  ISmartBroadcastPhone,
  ISmartBroadcastPhoneStats,
  ISmartBroadcastProblem,
  ISmartBroadcastIssue,
  ISmartBroadcastRendered,
  ISmartBroadcastSent,
  SmartBroadcastStep,
} from '../../types/smartBroadcast';
import { SmsParts } from '../../../utils/sms';
import { extractMessageAttributes, toCsv } from '../../../utils/utils';
import {
  MAX_ROWS,
  SheetData,
  exactParts,
  fieldNames,
  firstUsefulSheet,
  guessPhoneColumn,
  readPhone,
  readSheet,
  readWorkbook,
  renderMessage,
  sheetSize,
  toCountryCode,
  utf8Bytes,
} from './smart-broadcast.utils';

/** A row's finished message and what it costs. */
export interface SmartBroadcastMessage extends ISmartBroadcastRendered {
  row: number;
  parts: SmsParts;
}

export interface SmartBroadcastTotals {
  messages: number;
  parts: number;
  /** Parts most rows use; rows above it are flagged. */
  usualParts: number;
  maxParts: number;
  /** Row index of the most expensive message, or -1. */
  longest: number;
  multiPart: number;
  unicode: number;
  /** Rows sending at least one field as written. */
  withEmpty: number;
}

export interface SmartBroadcastDraftInfo {
  draft: ISmartBroadcastDraft;
  /** The sheet itself is still in this tab, so nothing needs uploading. */
  sheetKept: boolean;
}

/**
 * Gateway replies that mean the network did not take the message. Each must
 * be a DeliveryStatus enum name on the server: an unknown name fails the
 * whole count request.
 */
export const NOT_ACCEPTED_STATES = [
  'REJECTED',
  'UNDELIVERABLE',
  'UNKNOWN',
  'EXPIRED',
  'DELETED',
  'SKIPPED',
];

const DRAFT_PREFIX = 'bk.smartBroadcast.draft.v1';
const SHEET_PREFIX = 'bk.smartBroadcast.sheet.v1';
/** Sheets bigger than this stay out of storage; the message is kept regardless. */
const MAX_SHEET_CHARS = 4_000_000;
const SETTLE_MS = 250;
const SAVE_MS = 600;

interface SheetSnapshot {
  v: 1;
  fileName: string;
  sheetName: string;
  headers: string[];
  rows: string[][];
  rowNumbers: number[];
  restored: number[];
  shortened: number[];
}

/**
 * Smart Broadcast: holds the wizard state and ships it to the backend.
 *
 * The server (SmartBroadcastServiceImpl) takes a CSV plus a template, then
 * for each row substitutes `{{column}}` and sends one SMS. It keeps a
 * placeholder as written when the cell is empty or the column is unknown,
 * skips rows with no number and sends everything else as it arrives. So the
 * cleaning happens here: numbers are normalised against a default country,
 * invalid and repeated numbers can be left out, empty cells can take a
 * fallback, and unmatchable headers get a safe field name, all before upload.
 *
 * State lives in signals so the per-row work (phone parsing, rendering and
 * SMS parts for every row) runs once per change instead of on every change
 * detection pass. The older plain-field API is kept as getters and setters.
 */
@Injectable({ providedIn: 'root' })
export class SmartBroadcastService {
  apiService = inject(ApiService);
  userService = inject(UserProfileService);
  settingsService = inject(SettingsService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  serviceName = 'blackkonnectcxpengagement';

  // ---- source state ---------------------------------------------------------
  readonly step = signal<SmartBroadcastStep>(0);
  private readonly _file = signal<File | null>(null);
  private readonly _fileName = signal('');
  readonly fileSize = signal(0);
  readonly sheetNames = signal<string[]>([]);
  readonly sheetSizes = signal<number[]>([]);
  readonly sheetName = signal('');
  private readonly _headers = signal<string[]>([]);
  private readonly _rows = signal<string[][]>([]);
  readonly rowNumbers = signal<number[]>([]);
  readonly skippedRows = signal(0);
  readonly droppedColumns = signal(0);
  private readonly restoredByColumn = signal<number[]>([]);
  private readonly shortenedByColumn = signal<number[]>([]);

  private readonly _phoneIdx = signal(-1);
  /** Set when the phone column was picked automatically: share of values that read as numbers. */
  readonly phoneAuto = signal<number | null>(null);
  readonly country = signal<CountryCode | ''>('');
  readonly dropInvalid = signal(true);
  readonly dedupe = signal(true);

  private readonly _senderId = signal('');
  private readonly _subject = signal('');
  private readonly _body = signal('');
  readonly fallbacks = signal<Record<string, string>>({});
  /** Body and fallbacks as of the last pause in typing; drives the per-row work. */
  private readonly settled = signal<{ body: string; fallbacks: Record<string, string> }>({
    body: '',
    fallbacks: {},
  });

  /** Row shown in the preview, shared by the Message and Review steps. */
  readonly previewRow = signal(0);

  private readonly _loading = signal(false);
  private readonly _error = signal('');
  readonly sent = signal<ISmartBroadcastSent | null>(null);
  /** The last send failed in a way that may still have reached the server. */
  readonly sendUnclear = signal(false);
  /** False when the sheet was too large to keep for a refresh. */
  readonly sheetKept = signal(true);

  /** Settings from a draft, applied once its sheet is uploaded again. */
  private pendingDraft: ISmartBroadcastDraft | null = null;
  private workbook: WorkBook | null = null;
  private serviceAccountId: string | null = null;
  private orgCountry: CountryCode | '' = '';
  private countryTouched = false;
  private phoneTouched = false;
  senderIdsRequested = false;
  /** Organisation and user the broadcast in memory belongs to. */
  private owner = '';

  private settleTimer?: ReturnType<typeof setTimeout>;
  private draftTimer?: ReturnType<typeof setTimeout>;
  private sheetTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    this.settingsService.getUserInstitutionData.subscribe((data: IUserOrganization | null) => {
      if (data?.attributes?.serviceAccountId) {
        this.serviceAccountId = data.attributes.serviceAccountId;
      }
      const code = toCountryCode(data?.countryCode);
      if (code) {
        this.orgCountry = code;
        // Follow the organisation (another sign-in may have changed it)
        // unless the user picked a country for this broadcast.
        if (!this.countryTouched && this.country() !== code) this.applyCountry(code);
      }
    });

    // Keep a draft on this device while there is unsent work.
    effect(() => {
      const draft = this.draftSnapshot();
      if (!this.isBrowser || !draft) return;
      clearTimeout(this.draftTimer);
      this.draftTimer = setTimeout(() => this.writeDraft(draft), SAVE_MS);
    });
    effect(() => {
      const rows = this._rows();
      const headers = this._headers();
      if (!this.isBrowser || !rows.length || !headers.length || this.sent()) return;
      clearTimeout(this.sheetTimer);
      this.sheetTimer = setTimeout(() => this.writeSheet(), SAVE_MS);
    });
  }

  get organizationId() {
    return this.userService.organizationId;
  }

  /**
   * True when the broadcast in memory was started by the signed-in user in
   * the same organisation. The store outlives a sign-out in the same tab, so
   * a different user must not pick up someone else's sheet and message.
   */
  ownedByCurrentUser(): boolean {
    return !!this.owner && this.owner === this.currentOwner();
  }

  private currentOwner(): string {
    try {
      const user = this.userService.userDetails;
      const org = String(this.organizationId ?? '');
      const who = String(user?.sub ?? user?.preferred_username ?? '');
      return org || who ? `${org}|${who}` : '';
    } catch {
      return '';
    }
  }

  // ---- legacy plain-field API (kept for compatibility) ----------------------
  get file(): File | null {
    return this._file();
  }
  set file(value: File | null) {
    this._file.set(value);
  }
  get fileName(): string {
    return this._fileName();
  }
  set fileName(value: string) {
    this._fileName.set(value);
  }
  get headers(): string[] {
    return this._headers();
  }
  set headers(value: string[]) {
    this._headers.set(value);
  }
  get rows(): string[][] {
    return this._rows();
  }
  set rows(value: string[][]) {
    this._rows.set(value);
  }
  /** Header of the phone column, as written in the sheet. */
  get phoneColumn(): string {
    return this._headers()[this._phoneIdx()] ?? '';
  }
  set phoneColumn(value: string) {
    this.setPhoneColumn(this._headers().indexOf(value));
  }
  get senderId(): string {
    return this._senderId();
  }
  set senderId(value: string) {
    this._senderId.set(value);
  }
  get messageSubject(): string {
    return this._subject();
  }
  set messageSubject(value: string) {
    this._subject.set(value);
  }
  get messageBody(): string {
    return this._body();
  }
  set messageBody(value: string) {
    this._body.set(value);
    this.scheduleSettle();
  }
  get isLoading(): boolean {
    return this._loading();
  }
  set isLoading(value: boolean) {
    this._loading.set(value);
  }
  get errorMsg(): string {
    return this._error();
  }
  set errorMsg(value: string) {
    this._error.set(value);
  }

  // Signal reads for templates.
  readonly fileNameSig = this._fileName.asReadonly();
  readonly headersSig = this._headers.asReadonly();
  readonly rowsSig = this._rows.asReadonly();
  readonly phoneIndex = this._phoneIdx.asReadonly();
  readonly senderIdSig = this._senderId.asReadonly();
  readonly subjectSig = this._subject.asReadonly();
  readonly bodySig = this._body.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  // ---- derived state --------------------------------------------------------
  readonly hasRows = computed(() => this._headers().length > 0 && this._rows().length > 0);

  get hasData(): boolean {
    return this.hasRows();
  }

  /** Anything worth keeping: a sheet, a name or a message. */
  readonly hasWork = computed(
    () => this.hasRows() || !!this._body().trim() || !!this._subject().trim()
  );

  readonly columns = computed<ISmartBroadcastColumn[]>(() => {
    const headers = this._headers();
    const rows = this._rows();
    const restored = this.restoredByColumn();
    const shortened = this.shortenedByColumn();
    const names = fieldNames(headers);
    return headers.map((header, index) => {
      let filled = 0;
      let example = '';
      for (const row of rows) {
        const value = row[index] ?? '';
        if (value) {
          filled++;
          if (!example) example = value;
        }
      }
      return {
        index,
        header,
        field: names[index].field,
        renamed: names[index].renamed,
        filled,
        example,
        restored: restored[index] ?? 0,
        shortened: shortened[index] ?? 0,
      };
    });
  });

  readonly fields = computed(() => this.columns().map((column) => column.field));
  private readonly fieldIndex = computed(
    () => new Map(this.columns().map((column) => [column.field, column.index]))
  );
  /** Field name of the phone column; sent as `phoneColumn`. */
  readonly phoneField = computed(() => this.columns()[this._phoneIdx()]?.field ?? '');
  /**
   * The phone column as the page names it: its header, or its field name
   * when the header is blank. Test `phoneIndex() >= 0` for "chosen", not this.
   */
  readonly phoneLabel = computed(() => {
    const column = this.columns()[this._phoneIdx()];
    return column ? column.header || column.field : '';
  });

  readonly phones = computed<ISmartBroadcastPhone[]>(() => {
    const index = this._phoneIdx();
    const rows = this._rows();
    const country = this.country() || undefined;
    if (index < 0) {
      return rows.map(() => ({ raw: '', number: '', state: 'empty' as const, repeatOf: -1 }));
    }
    const seen = new Map<string, number>();
    return rows.map((row, i) => {
      const reading = readPhone(row[index] ?? '', country);
      let repeatOf = -1;
      if (reading.number) {
        const first = seen.get(reading.number);
        if (first === undefined) seen.set(reading.number, i);
        else repeatOf = first;
      }
      return { ...reading, repeatOf };
    });
  });

  /** Rows that will be uploaded and messaged, in sheet order. */
  readonly audience = computed<number[]>(() => {
    const dropInvalid = this.dropInvalid();
    const dedupe = this.dedupe();
    const out: number[] = [];
    this.phones().forEach((phone, i) => {
      // The server skips rows without a number, so they never go up.
      if (!phone.number) return;
      if (dropInvalid && phone.state !== 'valid') return;
      if (dedupe && phone.repeatOf >= 0) return;
      out.push(i);
    });
    return out;
  });

  private readonly audienceSet = computed(() => new Set(this.audience()));

  readonly numberStats = computed<ISmartBroadcastNumberStats>(() => {
    const dropInvalid = this.dropInvalid();
    const stats = { total: 0, valid: 0, invalid: 0, shortened: 0, empty: 0, repeats: 0, send: 0 };
    for (const phone of this.phones()) {
      stats.total++;
      if (phone.state === 'empty') {
        stats.empty++;
        continue;
      }
      if (phone.state === 'valid') stats.valid++;
      else stats.invalid++;
      if (phone.state === 'shortened') stats.shortened++;
      if (phone.repeatOf >= 0 && (phone.state === 'valid' || !dropInvalid)) stats.repeats++;
    }
    stats.send = this.audience().length;
    return stats;
  });

  /** Empty cells per field among the rows that will send. */
  readonly emptyByField = computed<Record<string, number>>(() => {
    const rows = this._rows();
    const phoneIdx = this._phoneIdx();
    const out: Record<string, number> = {};
    const audience = this.audience();
    for (const column of this.columns()) {
      if (column.index === phoneIdx) continue;
      let empty = 0;
      for (const i of audience) if (!rows[i][column.index]) empty++;
      out[column.field] = empty;
    }
    return out;
  });

  /** Fields the live message uses, split into columns and unknown names. */
  readonly usedFields = computed(() => {
    const fields = new Set(this.fields());
    const used = extractMessageAttributes(this._body());
    return {
      known: used.filter((field) => fields.has(field)),
      unknown: used.filter((field) => !fields.has(field)),
    };
  });

  /** True while typing: the per-row figures are about to update. */
  readonly settling = computed(() => {
    const settled = this.settled();
    return settled.body !== this._body() || settled.fallbacks !== this.fallbacks();
  });

  /** Each sending row's finished message, indexed by row (null when not sent). */
  readonly messages = computed<(SmartBroadcastMessage | null)[]>(() => {
    const { body, fallbacks } = this.settled();
    const rows = this._rows();
    const out = new Array<SmartBroadcastMessage | null>(rows.length).fill(null);
    if (!body.trim()) return out;
    for (const row of this.audience()) {
      const rendered = this.renderWith(row, body, fallbacks);
      out[row] = { ...rendered, row, parts: exactParts(rendered.text) };
    }
    return out;
  });

  readonly totals = computed<SmartBroadcastTotals>(() => {
    const totals: SmartBroadcastTotals = {
      messages: 0,
      parts: 0,
      usualParts: 0,
      maxParts: 0,
      longest: -1,
      multiPart: 0,
      unicode: 0,
      withEmpty: 0,
    };
    const histogram = new Map<number, number>();
    for (const message of this.messages()) {
      if (!message) continue;
      const parts = message.parts.parts;
      totals.messages++;
      totals.parts += parts;
      if (parts > 1) totals.multiPart++;
      if (message.parts.encoding === 'Unicode') totals.unicode++;
      if (message.empty.length) totals.withEmpty++;
      if (parts > totals.maxParts) {
        totals.maxParts = parts;
        totals.longest = message.row;
      }
      histogram.set(parts, (histogram.get(parts) ?? 0) + 1);
    }
    let best = 0;
    histogram.forEach((count, parts) => {
      if (count > best || (count === best && parts < totals.usualParts)) {
        best = count;
        totals.usualParts = parts;
      }
    });
    return totals;
  });

  /** Rows worth a look before sending, with what happens to each. */
  readonly problems = computed<ISmartBroadcastProblem[]>(() => {
    const phones = this.phones();
    const messages = this.messages();
    const rowNumbers = this.rowNumbers();
    const { usualParts } = this.totals();
    const dropInvalid = this.dropInvalid();
    const dedupe = this.dedupe();
    const hasColumn = this._phoneIdx() >= 0;
    const out: ISmartBroadcastProblem[] = [];

    phones.forEach((phone, row) => {
      const issues: ISmartBroadcastIssue[] = [];
      if (hasColumn && phone.state === 'empty') {
        issues.push({ kind: 'number', tone: 'idle', text: 'No number', outcome: 'Skipped' });
      } else if (phone.state === 'shortened') {
        issues.push({
          kind: 'number',
          tone: 'bad',
          text: `Shortened by Excel to ${phone.raw}`,
          outcome: dropInvalid ? 'Left out' : 'Sent as written, will fail',
        });
      } else if (phone.state === 'invalid') {
        issues.push({
          kind: 'number',
          tone: 'bad',
          text: 'Not a valid phone number',
          outcome: dropInvalid ? 'Left out' : phone.number ? 'Sent as written, likely to fail' : 'Skipped',
        });
      }
      if (phone.repeatOf >= 0 && (phone.state === 'valid' || !dropInvalid)) {
        issues.push({
          kind: 'number',
          tone: 'idle',
          text: `Same number as row ${rowNumbers[phone.repeatOf] ?? phone.repeatOf + 1}`,
          outcome: dedupe ? 'Left out' : 'Gets another message',
        });
      }
      const message = messages[row];
      if (message) {
        if (message.empty.length) {
          const names = message.empty.map((field) => `{{${field}}}`).join(', ');
          issues.push({
            kind: 'field',
            tone: 'wait',
            text: `No value for ${names}`,
            outcome: `Shows ${names} as written`,
          });
        }
        if (message.parts.parts > Math.max(1, usualParts)) {
          const unicode = message.parts.encoding === 'Unicode' ? ', Unicode' : '';
          issues.push({
            kind: 'cost',
            tone: 'wait',
            text: `${message.parts.parts} SMS parts${unicode}`,
            outcome: usualParts
              ? `Costs ${message.parts.parts} parts, most rows cost ${usualParts}`
              : `Costs ${message.parts.parts} parts`,
          });
        }
      }
      if (issues.length) {
        out.push({
          row,
          sheetRow: rowNumbers[row] ?? row + 2,
          number: phone.raw,
          issues,
        });
      }
    });
    return out;
  });

  /** The CSV that will be uploaded, and its size. */
  readonly upload = computed(() => {
    const csv = this.buildCsv(this.settled().fallbacks);
    return { csv, bytes: utf8Bytes(csv), rows: this.audience().length };
  });

  // ---- legacy derived API ----------------------------------------------------
  columnIndex(column: string): number {
    return this._headers().indexOf(column);
  }

  /** Trimmed values of a given column across every row. */
  columnValues(column: string): string[] {
    const idx = this.columnIndex(column);
    if (idx < 0) return [];
    return this._rows().map((row) => (row[idx] ?? '').toString().trim());
  }

  /** Phone column values, normalised to +E.164 where they parse. */
  phoneValues(): string[] {
    return this.phones().map((phone) => phone.number);
  }

  /** Validity breakdown for the currently-selected phone column. */
  get phoneStats(): ISmartBroadcastPhoneStats {
    const stats = this.numberStats();
    return {
      total: stats.total,
      filled: stats.total - stats.empty,
      valid: stats.valid,
      invalid: stats.invalid,
      empty: this._phoneIdx() < 0 ? stats.total : stats.empty,
    };
  }

  /** Recipients we will actually message. */
  get recipientCount(): number {
    return this.audience().length;
  }

  /** A row as a `{ field: value }` record, phone column normalised. */
  rowRecord(index: number): Record<string, string> {
    const record: Record<string, string> = {};
    for (const column of this.columns()) record[column.field] = this.cell(index, column.index);
    return record;
  }

  /** The message row `index` receives, placeholders kept where the server keeps them. */
  previewMessage(index: number): string {
    return this.renderRow(index).text;
  }

  /** Attributes referenced in the body that aren't columns. */
  get unknownAttributes(): string[] {
    return this.usedFields().unknown;
  }

  // ---- rendering ------------------------------------------------------------
  /** Live render of one row with the current body and fallbacks. */
  renderRow(
    index: number,
    body: string = this._body(),
    fallbacks: Record<string, string> = this.fallbacks()
  ): ISmartBroadcastRendered {
    return this.renderWith(index, body, fallbacks);
  }

  isSending(row: number): boolean {
    return this.audienceSet().has(row);
  }

  private renderWith(
    row: number,
    body: string,
    fallbacks: Record<string, string>
  ): ISmartBroadcastRendered {
    const lookup = this.fieldIndex();
    return renderMessage(
      body,
      (field) => {
        const index = lookup.get(field);
        return index === undefined ? undefined : this.cell(row, index);
      },
      fallbacks
    );
  }

  /** A cell as it will be uploaded (phone column normalised). */
  private cell(row: number, column: number): string {
    if (column === this._phoneIdx()) return this.phones()[row]?.number ?? '';
    return (this._rows()[row]?.[column] ?? '').toString();
  }

  // ---- mutations --------------------------------------------------------------
  /** Legacy entry point: replaces the data and clears downstream choices. */
  setFileData(fileName: string, headers: string[], rows: string[][], file: File | null) {
    this.applySheet(fileName, {
      headers,
      rows,
      rowNumbers: rows.map((_, i) => i + 2),
      restored: [],
      shortened: [],
      skippedRows: 0,
      droppedColumns: 0,
    });
    this._file.set(file);
    this.fileSize.set(file?.size ?? 0);
  }

  /**
   * Reads an uploaded file. Returns a message saying what is wrong, or ''
   * when the sheet loaded.
   */
  loadFile(file: File, buffer: ArrayBuffer): string {
    const workbook = readWorkbook(buffer);
    const names = workbook.SheetNames ?? [];
    if (!names.length) return 'This file has no sheets. Export it again from your spreadsheet app and upload the new file.';
    const sheet = firstUsefulSheet(workbook);
    const data = readSheet(workbook, sheet);
    const problem = this.sheetProblem(data);
    if (problem) return problem;

    this.workbook = workbook;
    this.sheetNames.set(names);
    this.sheetSizes.set(names.map((name) => sheetSize(workbook, name)));
    this.sheetName.set(sheet);
    this._file.set(file);
    this.fileSize.set(file.size);
    this.applySheet(file.name, data);
    return '';
  }

  /** Switch to another sheet of the same workbook. */
  selectSheet(name: string): string {
    if (!this.workbook || !this.workbook.Sheets[name]) {
      return 'That sheet is no longer loaded. Upload the file again to pick another sheet.';
    }
    const data = readSheet(this.workbook, name);
    const problem = this.sheetProblem(data);
    if (problem) return problem;
    this.sheetName.set(name);
    this.applySheet(this._fileName(), data);
    return '';
  }

  /** True when another sheet can be picked without uploading again. */
  get canPickSheet(): boolean {
    return !!this.workbook && this.sheetNames().length > 1;
  }

  private sheetProblem(data: SheetData): string {
    if (!data.headers.length) {
      return 'This sheet is empty. Pick a sheet with a header row and at least one row of data.';
    }
    if (!data.rows.length) {
      return 'This sheet has a header row but no data under it. Add at least one row, then upload it again.';
    }
    // `truncated`: the read stopped at the row cap with data still running,
    // so blank or title rows could hide more rows past it.
    if (data.rows.length > MAX_ROWS || data.truncated) {
      return `This sheet has more than ${MAX_ROWS.toLocaleString('en-GB')} rows, the most one broadcast takes. Split the file and send it in parts.`;
    }
    return '';
  }

  private applySheet(fileName: string, data: SheetData) {
    this._fileName.set(fileName);
    this._headers.set(data.headers);
    this._rows.set(data.rows);
    this.rowNumbers.set(data.rowNumbers);
    this.restoredByColumn.set(data.restored);
    this.shortenedByColumn.set(data.shortened);
    this.skippedRows.set(data.skippedRows);
    this.droppedColumns.set(data.droppedColumns);
    this.previewRow.set(0);
    // A re-upload must not carry a stale column.
    this._phoneIdx.set(-1);
    this.phoneAuto.set(null);
    this.phoneTouched = false;

    const pending = this.pendingDraft;
    if (pending && sameHeaders(pending.headers, data.headers)) {
      this.applyDraftColumns(pending);
      this.pendingDraft = null;
    } else {
      this.guessPhone();
    }
  }

  setPhoneColumn(index: number, manual = true) {
    this._phoneIdx.set(index >= 0 && index < this._headers().length ? index : -1);
    if (manual) {
      this.phoneTouched = true;
      this.phoneAuto.set(null);
    }
  }

  setCountry(code: CountryCode | '') {
    this.countryTouched = true;
    this.applyCountry(code);
  }

  private applyCountry(code: CountryCode | '') {
    this.country.set(code);
    // Validity depends on the country, so an automatic pick may change.
    if (!this.phoneTouched && this.hasRows()) this.guessPhone();
  }

  private guessPhone() {
    const guess = guessPhoneColumn(this._headers(), this._rows(), this.country() || undefined);
    if (guess) {
      this._phoneIdx.set(guess.index);
      this.phoneAuto.set(guess.share);
    } else {
      this._phoneIdx.set(-1);
      this.phoneAuto.set(null);
    }
  }

  setBody(value: string) {
    this.messageBody = value;
  }

  setFallback(field: string, value: string) {
    this.fallbacks.update((current) => {
      const next = { ...current };
      if (value) next[field] = value;
      else delete next[field];
      return next;
    });
    this.scheduleSettle();
  }

  /** Bring the per-row figures up to date now (before review or send). */
  settleNow() {
    clearTimeout(this.settleTimer);
    this.settled.set({ body: this._body(), fallbacks: this.fallbacks() });
  }

  private scheduleSettle() {
    if (!this.isBrowser) {
      this.settleNow();
      return;
    }
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => this.settleNow(), SETTLE_MS);
  }

  goTo(step: SmartBroadcastStep) {
    if (step >= 3) this.settleNow();
    this.step.set(step);
  }

  /** The furthest step the current state allows. */
  readonly reachable = computed<SmartBroadcastStep>(() => {
    if (this.sent()) return 4;
    if (!this.hasRows()) return 0;
    if (this._phoneIdx() < 0 || !this.audience().length) return 1;
    if (!this._senderId() || !this._subject().trim() || !this._body().trim()) return 2;
    return 3;
  });

  reset() {
    clearTimeout(this.settleTimer);
    this.workbook = null;
    this._file.set(null);
    this._fileName.set('');
    this.fileSize.set(0);
    this.sheetNames.set([]);
    this.sheetSizes.set([]);
    this.sheetName.set('');
    this._headers.set([]);
    this._rows.set([]);
    this.rowNumbers.set([]);
    this.skippedRows.set(0);
    this.droppedColumns.set(0);
    this.restoredByColumn.set([]);
    this.shortenedByColumn.set([]);
    this._phoneIdx.set(-1);
    this.phoneAuto.set(null);
    this.phoneTouched = false;
    this.countryTouched = false;
    this.country.set(this.orgCountry);
    this.dropInvalid.set(true);
    this.dedupe.set(true);
    this._senderId.set('');
    this._subject.set('');
    this._body.set('');
    this.fallbacks.set({});
    this.settled.set({ body: '', fallbacks: {} });
    this.previewRow.set(0);
    this._error.set('');
    this._loading.set(false);
    this.sent.set(null);
    this.sendUnclear.set(false);
    this.sheetKept.set(true);
    this.pendingDraft = null;
    this.senderIdsRequested = false;
    this.owner = this.currentOwner();
    this.step.set(0);
  }

  /** Start over: forget the broadcast here and on this device. */
  discard() {
    this.clearDraft();
    this.reset();
  }

  // ---- send -----------------------------------------------------------------
  send(): Observable<any> {
    this.settleNow();
    this._loading.set(true);
    this._error.set('');

    const subject = this._subject().trim();
    const form = new FormData();
    const upload = this.buildCleanedUpload();
    if (upload) {
      form.append('file', upload, upload.name);
    }
    form.append('message', this._body());
    form.append('subject', subject);
    form.append('senderId', this._senderId());
    form.append('phoneColumn', this.phoneField() || this.phoneColumn);
    form.append('attributes', JSON.stringify(extractMessageAttributes(this._body())));

    return this.apiService
      .post(
        `${this.serviceName}/api/v1/organization/${this.organizationId}/smart-broadcasts`,
        form,
        this.multipartHeaders()
      )
      .pipe(
        tap({
          next: () => {
            this._loading.set(false);
            this.sendUnclear.set(false);
          },
          error: (err) => {
            this._loading.set(false);
            this.sendUnclear.set(outcomeUnknown(err));
            this._error.set(describeSendError(err, subject));
          },
        })
      );
  }

  /** Record a 202: the server has the file and works through it row by row. */
  markSent(base: { total: number | null; notAccepted: number | null }) {
    // A name nobody used has no refusals either.
    const notAccepted = base.notAccepted ?? (base.total === 0 ? 0 : null);
    this.sent.set({
      subject: this._subject().trim(),
      senderId: this._senderId(),
      fileName: this._fileName(),
      expected: this.audience().length,
      parts: this.totals().parts,
      startedAt: new Date().toISOString(),
      baseTotal: base.total,
      baseNotAccepted: notAccepted,
    });
    this.clearDraft();
    this.step.set(4);
  }

  /**
   * Messages carrying this broadcast name (org-scoped count). The server
   * stores one message per row with the name as its subject, so this is how
   * progress is read. Null when the count is unavailable.
   */
  countMessages(subject: string, states?: string[]): Observable<number | null> {
    const params: Record<string, string | string[]> = { 'messageSubject.equals': subject };
    if (states?.length) params['deliveryStatus.in'] = states;
    return this.apiService
      .get<unknown>(
        `${this.serviceName}/api/organization/${this.organizationId}/engagement-messages/count`,
        params
      )
      .pipe(
        map((value) => {
          const n = typeof value === 'number' ? value : Number(value);
          return typeof value !== 'object' && Number.isFinite(n) ? n : null;
        }),
        catchError(() => of(null))
      );
  }

  /** Totals already using a name, read just before sending. */
  countBaseline(subject: string): Observable<{ total: number | null; notAccepted: number | null }> {
    return forkJoin({
      total: this.countMessages(subject),
      notAccepted: this.countMessages(subject, NOT_ACCEPTED_STATES),
    }).pipe(
      timeout(6000),
      catchError(() => of({ total: null, notAccepted: null }))
    );
  }

  /**
   * The upload: the audience rows only, the phone column normalised, empty
   * cells filled with their fallbacks and the header row using field names.
   */
  private buildCleanedUpload(): File | null {
    if (!this.hasRows()) return this._file();
    const csv = this.upload().csv;
    const base = this._fileName().replace(/\.[^.]+$/, '') || 'smart-broadcast';
    return new File([csv], `${base}.csv`, { type: 'text/csv' });
  }

  private buildCsv(fallbacks: Record<string, string>): string {
    const columns = this.columns();
    if (!columns.length) return '';
    const rows = this.audience().map((row) =>
      columns.map((column) => this.cell(row, column.index) || (fallbacks[column.field] ?? '').trim())
    );
    return toCsv(
      columns.map((column) => column.field),
      rows
    );
  }

  /**
   * Headers for the multipart upload. We return a (possibly empty) HttpHeaders so
   * ApiService does NOT fall back to its `application/json` default — that would
   * stop the browser setting the multipart boundary.
   */
  private multipartHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    if (this.serviceAccountId) {
      headers = headers.set('X-ServiceAccount-ID', this.serviceAccountId);
    }
    return headers;
  }

  // ---- draft kept on this device --------------------------------------------
  /**
   * The message and settings go to localStorage; the sheet, which holds
   * people's numbers, goes to sessionStorage so it leaves with the tab.
   */
  private readonly draftSnapshot = computed<ISmartBroadcastDraft | null>(() => {
    if (!this.hasWork() || this.sent()) return null;
    return {
      v: 1,
      savedAt: '',
      step: this.step(),
      fileName: this._fileName(),
      fileSize: this.fileSize(),
      sheetName: this.sheetName(),
      headers: this._headers(),
      rowCount: this._rows().length,
      phoneHeader: this.phoneColumn,
      phoneIndex: this._phoneIdx(),
      country: this.country(),
      dropInvalid: this.dropInvalid(),
      dedupe: this.dedupe(),
      senderId: this._senderId(),
      subject: this._subject(),
      body: this._body(),
      fallbacks: this.fallbacks(),
    };
  });

  /** The kept draft, if there is one for this organisation. */
  peekDraft(): SmartBroadcastDraftInfo | null {
    const local = this.storage('local');
    if (!local) return null;
    try {
      const raw = local.getItem(this.key(DRAFT_PREFIX));
      if (!raw) return null;
      const draft = JSON.parse(raw) as ISmartBroadcastDraft;
      if (draft?.v !== 1 || !draft.savedAt) return null;
      if (!draft.body?.trim() && !draft.subject?.trim() && !draft.fileName) return null;
      return { draft, sheetKept: !!this.readSheetSnapshot(draft) };
    } catch {
      return null;
    }
  }

  /** Bring a kept draft back. Returns true when the sheet came back too. */
  restoreDraft(info: SmartBroadcastDraftInfo): boolean {
    const { draft } = info;
    const sheet = this.readSheetSnapshot(draft);
    // A saved country, including '' for "No default country", is the
    // user's choice for this broadcast; only an older draft without the
    // field falls back to the organisation's.
    const savedCountry = typeof draft.country === 'string';
    this.countryTouched = savedCountry;
    this.country.set(savedCountry ? toCountryCode(draft.country) : this.orgCountry);
    this.dropInvalid.set(draft.dropInvalid !== false);
    this.dedupe.set(draft.dedupe !== false);
    this._senderId.set(draft.senderId ?? '');
    this._subject.set(draft.subject ?? '');
    this._body.set(draft.body ?? '');
    this.fallbacks.set({ ...(draft.fallbacks ?? {}) });
    this.settleNow();

    if (sheet) {
      this.fileSize.set(draft.fileSize ?? 0);
      this.sheetName.set(sheet.sheetName);
      this.sheetNames.set(sheet.sheetName ? [sheet.sheetName] : []);
      this.pendingDraft = draft;
      this.applySheet(sheet.fileName, {
        headers: sheet.headers,
        rows: sheet.rows,
        rowNumbers: sheet.rowNumbers,
        restored: sheet.restored ?? [],
        shortened: sheet.shortened ?? [],
        skippedRows: 0,
        droppedColumns: 0,
      });
      this.step.set(Math.min(draft.step ?? 0, this.reachable()) as SmartBroadcastStep);
      return true;
    }

    // The sheet has to come back by upload; settings wait for it.
    this.pendingDraft = draft;
    this.step.set(0);
    return false;
  }

  /** Name of the file a restored draft is waiting for. */
  get awaitingFile(): string {
    return this.pendingDraft && !this.hasRows() ? this.pendingDraft.fileName : '';
  }

  private applyDraftColumns(draft: ISmartBroadcastDraft) {
    const index =
      draft.headers[draft.phoneIndex] === draft.phoneHeader
        ? draft.phoneIndex
        : this._headers().indexOf(draft.phoneHeader);
    if (index >= 0) {
      this._phoneIdx.set(index);
      this.phoneTouched = true;
      this.phoneAuto.set(null);
    } else {
      this.guessPhone();
    }
  }

  /** Write any pending draft now (leaving the page). */
  flushDraft() {
    if (!this.isBrowser) return;
    if (this.draftTimer) {
      clearTimeout(this.draftTimer);
      this.draftTimer = undefined;
      const draft = this.draftSnapshot();
      if (draft) this.writeDraft(draft);
    }
    if (this.sheetTimer) {
      clearTimeout(this.sheetTimer);
      this.sheetTimer = undefined;
      this.writeSheet();
    }
  }

  clearDraft() {
    clearTimeout(this.draftTimer);
    clearTimeout(this.sheetTimer);
    this.draftTimer = undefined;
    this.sheetTimer = undefined;
    try {
      this.storage('local')?.removeItem(this.key(DRAFT_PREFIX));
    } catch {
      /* storage blocked */
    }
    try {
      this.storage('session')?.removeItem(this.key(SHEET_PREFIX));
    } catch {
      /* storage blocked */
    }
  }

  private writeDraft(draft: ISmartBroadcastDraft) {
    this.draftTimer = undefined;
    if (this.sent()) return;
    try {
      this.storage('local')?.setItem(
        this.key(DRAFT_PREFIX),
        JSON.stringify({ ...draft, savedAt: new Date().toISOString() })
      );
    } catch {
      /* quota or blocked storage: the draft is a convenience */
    }
  }

  private writeSheet() {
    this.sheetTimer = undefined;
    const session = this.storage('session');
    if (!session || this.sent() || !this.hasRows()) return;
    const snapshot: SheetSnapshot = {
      v: 1,
      fileName: this._fileName(),
      sheetName: this.sheetName(),
      headers: this._headers(),
      rows: this._rows(),
      rowNumbers: this.rowNumbers(),
      restored: this.restoredByColumn(),
      shortened: this.shortenedByColumn(),
    };
    try {
      const json = JSON.stringify(snapshot);
      if (json.length > MAX_SHEET_CHARS) {
        session.removeItem(this.key(SHEET_PREFIX));
        this.sheetKept.set(false);
        return;
      }
      session.setItem(this.key(SHEET_PREFIX), json);
      this.sheetKept.set(true);
    } catch {
      this.sheetKept.set(false);
      try {
        session.removeItem(this.key(SHEET_PREFIX));
      } catch {
        /* storage blocked */
      }
    }
  }

  private readSheetSnapshot(draft: ISmartBroadcastDraft): SheetSnapshot | null {
    try {
      const raw = this.storage('session')?.getItem(this.key(SHEET_PREFIX));
      if (!raw) return null;
      const sheet = JSON.parse(raw) as SheetSnapshot;
      if (sheet?.v !== 1 || !Array.isArray(sheet.rows) || !Array.isArray(sheet.headers)) return null;
      if (sheet.fileName !== draft.fileName || !sameHeaders(sheet.headers, draft.headers)) return null;
      return sheet;
    } catch {
      return null;
    }
  }

  private storage(kind: 'local' | 'session'): Storage | null {
    if (!this.isBrowser || typeof window === 'undefined') return null;
    try {
      return (kind === 'local' ? window.localStorage : window.sessionStorage) ?? null;
    } catch {
      return null;
    }
  }

  private key(prefix: string): string {
    let org = '';
    try {
      org = String(this.organizationId ?? '');
    } catch {
      org = '';
    }
    return `${prefix}.${org || 'default'}`;
  }
}

function sameHeaders(a: string[] | undefined, b: string[] | undefined): boolean {
  if (!a || !b || a.length !== b.length) return false;
  return a.every((header, i) => header === b[i]);
}

/**
 * The upload may have reached the server even though no answer came back: a
 * dropped connection, or a gateway giving up while the server reads the file
 * (it answers 202 and then sends row by row).
 */
function outcomeUnknown(err: any): boolean {
  const status = Number(err?.status);
  return status === 0 || status === 502 || status === 504;
}

/** Send errors in words: what went wrong and what to do. */
function describeSendError(err: any, subject: string): string {
  const status = Number(err?.status);
  const server = [err?.error?.message, err?.error?.detail, err?.error?.title].find(
    (text) => typeof text === 'string' && text.trim() && !/^http failure/i.test(text)
  );
  if (outcomeUnknown(err)) {
    const lost = status === 0 ? 'The connection dropped' : 'The server didn’t answer in time';
    return `${lost}, so we can’t tell whether the broadcast started. Look in Messages for “${subject}” before sending again, or every row could get it twice.`;
  }
  if (status === 413) {
    return 'The upload is too large for the server, so nothing was sent. Split the sheet into smaller files and send each one.';
  }
  if (status === 401) {
    return 'Your session has ended, so nothing was sent. Sign in again, then send.';
  }
  if (status === 403) {
    return 'Your account isn’t allowed to send broadcasts. Ask an admin for the Compose SMS permission.';
  }
  if (status === 400) {
    return server
      ? `The server turned the broadcast down: ${server}`
      : 'The server turned the broadcast down. Check the sender ID and message, then try again.';
  }
  return server
    ? `The broadcast was not sent: ${server}`
    : 'The broadcast was not sent because of a server error. Nothing went out. Try again in a minute.';
}
